import React from 'react'
import AntdInput from 'antd/es/input'
import type { InputProps, InputRef } from 'antd/es/input'
import type { PasswordProps } from 'antd/es/input/Password'
import type { SearchProps } from 'antd/es/input/Search'
import type { TextAreaProps, TextAreaRef } from 'antd/es/input/TextArea'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { Input as ShadcnSeamInput } from './Input.shadcn'

/**
 * Compat seam leaf for antd's `antd/es/input` — one of the six presentational
 * components put behind the design toggle in the same PR (Input, Card, Alert,
 * Divider, Empty, Skeleton), after `./Button` (#970), `./Typography` +
 * `./Space` (#971) and `./Tag` (#972).
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` pulls antd/es/index.js — the whole component barrel —
 * into whatever chunk imports it, and this is the widest of the six (290
 * files, 692 tags). `update-design` ships a real seam at this same path with
 * the same shape — a NAMED `Input` and no default — so a redirected call site
 * needs no further change when that lands.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch each dispatcher below renders
 * antd's own component with the props it was given AND the ref it was given:
 * same element, same class names, same DOM, same imperative handle. The ref
 * matters more here than anywhere else in the seam — `src/ui/DebouncedSearch`
 * calls `.focus()` on it, and rc-field-form uses it to scroll to the first
 * invalid field — so the parity test asserts the handle as well as the markup.
 * `./__tests__/input-seam-parity.test.tsx` is the test to keep green.
 *
 * antd's four statics are carried across explicitly, NOT by copying antd's
 * component object: `Object.assign(dispatcher, AntdInput)` would also copy a
 * forwardRef object's `$$typeof` and `render` and turn this into something
 * React cannot render. `TextArea` (115 call sites), `Search` (9) and
 * `Password` (5) each dispatch exactly as the root does, so the seam cannot
 * end up half-switched.
 *
 * `Input.Group` — antd's deprecated addon group — is carried as an UNDISPATCHED
 * passthrough to antd's own. It has 0 call sites here and its replacement
 * (`addonBefore`/`addonAfter`) is fully ported, so writing a shadcn half for it
 * would be a surface nothing exercises; dropping it entirely would narrow the
 * type antd declares. It is named in the port's report as unported.
 * ------------------------------------------------------------------------- */
const InputDispatcher = React.forwardRef<InputRef, InputProps>((props, ref) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamInput {...props} ref={ref} />

  return <AntdInput {...props} ref={ref} />
})

InputDispatcher.displayName = 'Input'

const TextAreaDispatcher = React.forwardRef<TextAreaRef, TextAreaProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn')
      return <ShadcnSeamInput.TextArea {...props} ref={ref} />

    return <AntdInput.TextArea {...props} ref={ref} />
  }
)

TextAreaDispatcher.displayName = 'Input.TextArea'

const PasswordDispatcher = React.forwardRef<InputRef, PasswordProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn')
      return <ShadcnSeamInput.Password {...props} ref={ref} />

    return <AntdInput.Password {...props} ref={ref} />
  }
)

PasswordDispatcher.displayName = 'Input.Password'

const SearchDispatcher = React.forwardRef<InputRef, SearchProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn')
      return <ShadcnSeamInput.Search {...props} ref={ref} />

    return <AntdInput.Search {...props} ref={ref} />
  }
)

SearchDispatcher.displayName = 'Input.Search'

export const Input = Object.assign(InputDispatcher, {
  TextArea: TextAreaDispatcher,
  Password: PasswordDispatcher,
  Search: SearchDispatcher,
  Group: AntdInput.Group,
})
